import {useMemo} from 'react';

import {useSitePreference} from '@amblique/base/app/hooks/use-site-preference';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';

import {BNPL_DEFAULTS, BNPL_PREFERENCE_KEYS as KEYS} from '@amblique/king-living/app/constants/bnpl-messaging';
import {getZipRegion, normalizeSitePreferenceEnum} from '@amblique/king-living/app/utils/bnpl-messaging-utils';

const isEnabled = (value) => value === true || value === 'true';

export function useBnplMessagingConfig() {
  const {site} = useMultiSite();
  const siteId = site?.id || 'kl-au';

  const afterpayEnabled = useSitePreference(KEYS.AFTERPAY_ENABLED, false);
  const afterpayMpid = useSitePreference(KEYS.AFTERPAY_MPID, null);
  const afterpayScriptUrl = useSitePreference(KEYS.AFTERPAY_SCRIPT_URL, BNPL_DEFAULTS.AFTERPAY_SCRIPT_URL);
  const afterpayScriptMin = useSitePreference(KEYS.AFTERPAY_MIN_AMOUNT, BNPL_DEFAULTS.AFTERPAY_MIN_AMOUNT);
  const afterpayScriptMax = useSitePreference(KEYS.AFTERPAY_MAX_AMOUNT, BNPL_DEFAULTS.AFTERPAY_MAX_AMOUNT);
  const afterpayPlatform = useSitePreference(KEYS.AFTERPAY_PLATFORM, BNPL_DEFAULTS.AFTERPAY_PLATFORM);

  const zipEnabled = useSitePreference(KEYS.ZIP_ENABLED, false);
  const zipMerchantKey = useSitePreference(KEYS.ZIP_MERCHANT_KEY, null);
  const zipEnvironmentRaw = useSitePreference(KEYS.ZIP_ENVIRONMENT, BNPL_DEFAULTS.ZIP_ENVIRONMENT);
  const zipScriptUrl = useSitePreference(KEYS.ZIP_SCRIPT_URL, BNPL_DEFAULTS.ZIP_SCRIPT_URL);

  return useMemo(() => {
    const zipRegion = getZipRegion(siteId);
    const zipEnvironment =
      normalizeSitePreferenceEnum(zipEnvironmentRaw) || BNPL_DEFAULTS.ZIP_ENVIRONMENT;

    return {
      afterpay: {
        enabled: isEnabled(afterpayEnabled) && Boolean(afterpayMpid),
        mpid: afterpayMpid || null,
        scriptUrl: afterpayScriptUrl || BNPL_DEFAULTS.AFTERPAY_SCRIPT_URL,
        scriptMin: String(afterpayScriptMin ?? BNPL_DEFAULTS.AFTERPAY_MIN_AMOUNT),
        scriptMax: String(afterpayScriptMax ?? BNPL_DEFAULTS.AFTERPAY_MAX_AMOUNT),
        platform: afterpayPlatform || BNPL_DEFAULTS.AFTERPAY_PLATFORM,
      },
      zip: {
        // Zip merchant public key is required for AU stores only.
        enabled: isEnabled(zipEnabled) && Boolean(zipRegion && (zipRegion === 'nz' || zipMerchantKey)),
        merchantPublicKey: zipMerchantKey || null,
        region: zipRegion,
        environment: zipEnvironment,
        scriptUrl: zipScriptUrl || BNPL_DEFAULTS.ZIP_SCRIPT_URL,
      },
    };
  }, [
    afterpayEnabled,
    afterpayMpid,
    afterpayPlatform,
    afterpayScriptMax,
    afterpayScriptMin,
    afterpayScriptUrl,
    siteId,
    zipEnabled,
    zipEnvironmentRaw,
    zipMerchantKey,
    zipScriptUrl,
  ]);
}
